/**
 * LocationSearch.tsx
 *
 * This component provides an interface for users to search and select a location.
 * It integrates with the HERE Maps API for address autocomplete and geocoding,
 * and includes a map display using the HereMaps component.
 *
 * The component allows users to:
 * - Search for addresses using an autocomplete feature
 * - Select between address/tax ID search and latitude/longitude input
 * - View and interact with a map to set the location
 * - See enriched data for selected locations, including tax information and listings
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ListingResource,
  useFieldUpdater,
  useListingField,
  useStatusFlag,
} from '@brightmls/nextgen.lae.flc';
import { useDraftListing } from '../../../contexts/DraftListingContext';
import HereMaps from '../../common/HereMaps';
import { PropertyField } from '../../../system/field';
import {
  AutocompleteResult,
  geocodeLocationId,
  geocodePartialAddress,
  reverseGeocode,
  searchAddressHere,
} from '../../../utils/hereGeocoding';
import { SearchAddressResult } from '../../../system/search';
import debounce from 'lodash/debounce';
import { useService } from '../../../contexts/ServiceContext';
import SearchIcon from '@mui/icons-material/Search';
import {
  buildBoundingBox,
  formatAddressLineOne,
  formatCounty,
  formatState,
  formatStreet,
  formatZipCode,
  isValidLocationMatch,
} from '../../../system/location';
import { useMetadata } from '../../../contexts/MetadataContext';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../flags';
import { NoTaxRecordFormLink } from '../../common/form/NoTaxRecordForm';
import { generateDataTestId, isValidNumber } from '../../../utils';
import { useModal } from '../../../contexts/ModalContext';
import { TAX_SEARCH_THRESHOLD } from '../../../config';
import { EnrichedAutocompleteResult } from './types';
import AddressSearchField from './components/AddressSearchField';
import CoordinatesField from './components/CoordinatesField';
import AddressTaxIdSelector from './components/AddressTaxIdSelector';
import TaxIdSearchField from './components/TaxIdSearchField';
import FormAccordion from '../../common/FormAccordion';
import HereMapsOptimized from 'src/components/common/HereMapsOptimized';
import { useTenant } from '../../../contexts/TenantContext';
import { FALLBACK_COVERAGE_CENTER } from './constants';

export const MAX_LAT_LNG_LENGTH = 12;

const LocationSearch: React.FC = () => {
  // Tap in to the theme to perform conditional rendering based on the screen size
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { getLocationConfigValue, getConfigValue } = useTenant();

  // pluck tenant config for location boundaries
  const tenantLocationCoverageCenter =
    getLocationConfigValue('LOCATION_COVERAGE_CENTER') ||
    FALLBACK_COVERAGE_CENTER;

  const tenantLocationOfficeCenter =
    getLocationConfigValue('LOCATION_HOME_OFFICE') ||
    tenantLocationCoverageCenter;

  // pluck tenant config for system fields
  const tenantSystemLocale = getConfigValue<string>('RESO_SYSTEM_LOCALE');
  const tenantSubSystemLocale = getConfigValue<string>(
    'RESO_SUB_SYSTEM_LOCALE',
  );
  const tenantBusinessPartner = getConfigValue<string>(
    'RESO_SOURCE_BUSINESS_PARTNER',
  );

  // pluck tenant config for public record strategy
  const tenantPublicRecordSystem = getConfigValue<string>(
    'PUBLIC_RECORD_SERVICE',
  );

  // pluck tenant config for lookup values for street types and direction
  const tenantLookupStreetTypes = getConfigValue<string>(
    'LOCATION_LOOKUP_STREET_TYPES',
    'Street_Types',
  );

  const tenantLookupStreetDirections = getConfigValue<string>(
    'LOCATION_LOOKUP_STREET_DIRECTIONS',
    'Street_Directions',
  );

  // pluck tenant config for county resolver
  const tenantCountyResolutionMap = getConfigValue<string>(
    'LOCATION_COUNTY_RESOLUTION_MAP',
    undefined,
  );

  // seek the config override if provided, or use build time variable for HERE api key
  const { getFeatureValue, hasFeature } = useFeatureFlags();
  const { HERE_API_KEY } = getFeatureValue(
    FeatureFlags.NGW_CONFIG_OVERRIDE,
  ) || { HERE_API_KEY: process.env.REACT_APP_HERE_API_KEY };

  // Field subscriptions - only subscribe to fields we need
  const latitudeField = useListingField(
    ListingResource.Property,
    PropertyField.LATITUDE,
    undefined,
  );
  const longitudeField = useListingField(
    ListingResource.Property,
    PropertyField.LONGITUDE,
    undefined,
  );
  const unparsedAddressField = useListingField(
    ListingResource.Property,
    PropertyField.UNPARSED_ADDRESS,
    '',
  );

  // Establish UI flow based on public record system
  const isRealistPublicRecords = tenantPublicRecordSystem === 'REALIST';

  // Write-only field updater for batch operations
  const updateFields = useFieldUpdater();

  // Status management using new hooks
  const noTaxRecordMode = useStatusFlag('no_tax_record_mode', false);

  // Access functions in the draft listing
  const { setLocation, clearListing } = useDraftListing();

  // Access functions from the backend service
  const { searchLocation, searchLocationByTaxId } = useService();

  const { showModal } = useModal();

  // Access lookups for address parsing
  const { lookup } = useMetadata();

  // State for controlling the type of location input (address or coordinates)
  const [locationType, setLocationType] = useState('address');

  // State for Realist search mode (address vs taxId) - only used when isRealistPublicRecords is true
  const [searchMode, setSearchMode] = useState<'address' | 'taxId'>('address');
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [taxIdInputValue, setTaxIdInputValue] = useState('');
  const [isTaxIdLoading, setIsTaxIdLoading] = useState(false);
  const [taxOptions, setTaxOptions] = useState<EnrichedAutocompleteResult[]>(
    [],
  );

  const noTaxRecordModeRef = useRef(noTaxRecordMode.isSet);

  const isOptimizedEnabled = hasFeature(
    FeatureFlags.NGW_HEREMAPS_OPTIMIZATIONS,
  );

  // Update ref when status changes
  useEffect(() => {
    noTaxRecordModeRef.current = noTaxRecordMode.isSet;
  }, [noTaxRecordMode.isSet]);

  // State for latitude and longitude, initialized with default or existing values
  const [latitude, setLatitude] = useState<null | string | number>(() => {
    const fieldValue = latitudeField.value;
    return fieldValue === null || fieldValue === undefined
      ? ''
      : `${fieldValue}`;
  });

  const [longitude, setLongitude] = useState<null | string | number>(() => {
    const fieldValue = longitudeField.value;
    return fieldValue === null || fieldValue === undefined
      ? ''
      : `${fieldValue}`;
  });

  // State for the currently selected address
  const [, setAddress] = useState(() => unparsedAddressField.value);

  // State for the list of address options displayed in the autocomplete
  const [addressOptions, setAddressOptions] = useState<
    EnrichedAutocompleteResult[]
  >([]);

  // State for the current input value in the autocomplete field
  const [inputValue, setInputValue] = useState('');

  // Start zoomed out to show the coverage area
  const [mapZoom, setMapZoom] = useState(10);

  // State for the currently selected prediction from the autocomplete
  const [selectedPrediction, setSelectedPrediction] =
    useState<EnrichedAutocompleteResult | null>(null);

  // State to indicate if the component is currently loading data
  const [isLoading, setIsLoading] = useState(false);

  // Ref to keep track of the latest input value for race condition prevention
  const latestInputRef = useRef('');

  // Keep track of the pending location update
  const [pendingLocationUpdate, setPendingLocationUpdate] =
    useState<EnrichedAutocompleteResult | null>(null);

  // Let the user know if tax search is pending, this is in the background so we want the UX to be okay
  const [isTaxSearchPending, setIsTaxSearchPending] = useState(false);

  // Track if this is the first map interaction
  const [isFirstMapInteraction, setIsFirstMapInteraction] = useState(true);

  const createWithoutTaxRecord = hasFeature(
    FeatureFlags.NGW_CREATE_WITHOUT_TAX_RECORD,
  );

  // Store lookups for directions and street types
  const lookups = {
    street_types: lookup(tenantLookupStreetTypes)!,
    street_directions: lookup(tenantLookupStreetDirections)!,
  };

  /**
   * Formats an address object into a readable string.
   * @param result The EnrichedAutocompleteResult to format
   * @returns A string representation of the address
   */
  const formatAddress = (result: EnrichedAutocompleteResult): string => {
    const addressToUse = result.address;
    const unitNumber = addressToUse.unitNumber
      ? `#${addressToUse.unitNumber}`
      : null;

    const parts = [
      [addressToUse.houseNumber, addressToUse.street, unitNumber]
        .filter(Boolean)
        .join(' '),
      addressToUse.city,
      addressToUse.state,
      addressToUse.postalCode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const clear = function () {
    setSelectedPrediction(null);
    // Clear location-specific fields
    clearListing();
  };

  const enrichHereResult = useCallback(
    async (
      hereResult: AutocompleteResult,
    ): Promise<EnrichedAutocompleteResult> => {
      // Perform enrichment for HERE results
      const geocodedLocation = await geocodeLocationId(
        HERE_API_KEY,
        hereResult.locationId,
      );

      const enrichedData = await searchLocation(
        hereResult.label,
        geocodedLocation?.displayPosition.Latitude || 0,
        geocodedLocation?.displayPosition.Longitude || 0,
        {
          houseNumber: hereResult.address.houseNumber || '',
          street: hereResult.address.street,
          city: hereResult.address.city,
          county: hereResult.address.county,
          state: hereResult.address.state,
          zipcode: hereResult.address.postalCode,
          unitNumber: hereResult.address.unitNumber,
        },
      );

      return {
        ...hereResult,
        geocodedLocation,
        enrichedData,
      };
    },
    [HERE_API_KEY, searchLocation],
  );

  /**
   * Handle the first map interaction - zoom in to the normal level
   */
  const handleFirstMapInteraction = useCallback(() => {
    if (isFirstMapInteraction) {
      setMapZoom(15); // Zoom in to standard level after first interaction
      setIsFirstMapInteraction(false);
    }
  }, [isFirstMapInteraction]);

  const enrichTaxResult = useCallback(
    async (
      taxResult: SearchAddressResult,
    ): Promise<EnrichedAutocompleteResult> => {
      // Use HERE API to get missing details
      const geocodedResult = await geocodePartialAddress(HERE_API_KEY, {
        street: taxResult.Address.Street,
        city: taxResult.Address.City,
        county: taxResult.Address.County,
        state: taxResult.Address.State,
        postalCode: taxResult.Address.Zip,
        country: 'USA', // Assuming all tax results are for USA
      });

      if (
        geocodedResult &&
        isValidLocationMatch(
          { state: geocodedResult.state },
          { state: taxResult.Address.State },
        )
      ) {
        return {
          label: geocodedResult.label,
          language: 'en',
          countryCode: geocodedResult.countryCode,
          locationId: `tax-${taxResult?.TaxRecord?.TaxId}`,
          address: {
            country: geocodedResult.countryName,
            state: geocodedResult.state,
            county: geocodedResult.county,
            city: geocodedResult.city,
            street: geocodedResult.street,
            postalCode: geocodedResult.postalCode,
            houseNumber: geocodedResult.houseNumber,
            unitNumber: taxResult.Address.UnitNumber,
          },
          matchLevel: 'street',
          enrichedData: taxResult,
          geocodedLocation: {
            locationId: geocodedResult.locationId,
            locationType: geocodedResult.locationType,
            displayPosition: {
              Latitude: geocodedResult.latitude,
              Longitude: geocodedResult.longitude,
            },
          } as any,
        };
      } else {
        // If geocoding fails, return a result with available tax data
        return {
          label: taxResult.Address.Street,
          language: 'en',
          countryCode: 'USA',
          locationId: `tax-${taxResult?.TaxRecord?.TaxId}`,
          address: {
            country: 'United States',
            state: taxResult.Address.State,
            county: taxResult.Address.County,
            city: taxResult.Address.City || '',
            street: taxResult.Address.Street,
            postalCode: taxResult.Address.Zip || '',
            houseNumber: '',
            unitNumber: taxResult.Address.UnitNumber || '',
          },
          matchLevel: 'street',
          enrichedData: taxResult,
          geocodedLocation: {
            locationId: `tax-${taxResult?.TaxRecord?.TaxId}`,
            locationType: 'address',
            displayPosition: {
              Latitude: taxResult.Address.Latitude,
              Longitude: taxResult.Address.Longitude,
            },
          } as any,
        };
      }
    },
    [HERE_API_KEY],
  );

  /**
   * Handles Tax ID search with county for Realist flow
   */
  const handleTaxIdSearch = useCallback(
    async (taxId: string, county: string) => {
      if (!taxId.trim() || !county || taxId.trim().length < 3) {
        setTaxOptions([]);
        return;
      }

      setIsTaxIdLoading(true);
      try {
        // Call searchLocationByTaxId with county parameter (you'll expand this method)
        const results = await searchLocationByTaxId(taxId, county);

        if (results && results.length > 0) {
          // Enrich all tax results and display them
          const enrichedResults = await Promise.all(
            results.map(enrichTaxResult),
          );
          setTaxOptions(enrichedResults);
        } else {
          setTaxOptions([]);
        }
      } catch (error) {
        console.error('Error searching by Tax ID:', error);
        setTaxOptions([]);
      } finally {
        setIsTaxIdLoading(false);
      }
    },
    [searchLocationByTaxId, enrichTaxResult],
  );

  const compareEnrichedResults = (
    option: EnrichedAutocompleteResult,
    value: EnrichedAutocompleteResult,
  ) => {
    function cleanZip(zipcode: string) {
      return zipcode && zipcode.includes('-') ? zipcode.split('-')[0] : zipcode;
    }
    return (
      option.address.country === value.address.country &&
      option.address.state === value.address.state &&
      option.address.city?.toUpperCase() ===
        value.address.city?.toUpperCase() &&
      option.address.street === value.address.street &&
      cleanZip(option.address.postalCode) ===
        cleanZip(value.address.postalCode) &&
      option.address.houseNumber === value.address.houseNumber
    );
  };

  /**
   * Handles changes to the location, updating relevant state and fetching enriched data.
   * If no address is provided, it performs reverse geocoding.
   * @param newAddress The new address string (can be empty)
   * @param lat The new latitude
   * @param lng The new longitude
   */
  const handleLocationChange = useCallback(
    async (newAddress: string, lat: number | null, lng: number | null) => {
      clear();

      setLatitude(lat === null ? '' : lat.toString());
      setLongitude(lng === null ? '' : lng.toString());

      updateFields({
        LATITUDE: lat,
        LONGITUDE: lng,
      });

      if (lat !== null && lng !== null) {
        let addressResult: EnrichedAutocompleteResult | null =
          await reverseGeocode(HERE_API_KEY, lat, lng);
        if (addressResult) {
          setAddress(addressResult.label);
          unparsedAddressField.updateValue(addressResult.label);
          setInputValue(addressResult.label);
          setSelectedPrediction(addressResult);
          setAddressOptions([addressResult]);
          updateAddressFields(addressResult);
        } else if (!addressResult && noTaxRecordModeRef.current) {
          showModal({
            title: 'No location found with Lat/Lng provided', // Optional title
            content: (
              <div>
                Please, provide correct Latitude and Longitude values to find a
                correct location
              </div>
            ),
            cancelText: 'Close',
          });
        }

        try {
          const result = await searchLocation(
            addressResult?.label || '',
            lat,
            lng,
          );
          if (result) {
            setLocation(result);
          }
        } catch (error) {
          console.error('Error fetching enriched data:', error);
        }
      }
    },
    [clear, HERE_API_KEY, searchLocation, setLocation, showModal, updateFields],
  );

  /**
   * Handles the selection of an address from the autocomplete options.
   * Updates relevant state and fetches additional data if needed.
   * @param event The React synthetic event
   * @param value The selected EnrichedAutocompleteResult
   */
  const handleAddressChange = useCallback(
    async (
      event: React.SyntheticEvent,
      value: EnrichedAutocompleteResult | null,
    ) => {
      // Trigger a map interaction
      handleFirstMapInteraction();

      clear();

      if (value) {
        setSelectedPrediction(value);
        updateAddressFields(value);
        setAddress(value.label);
        setInputValue(value.label);

        if (value.geocodedLocation?.displayPosition) {
          const { Latitude, Longitude } =
            value.geocodedLocation.displayPosition;
          setLatitude(Latitude);
          setLongitude(Longitude);
        }

        if (value.enrichedData) {
          setLocation(value.enrichedData);
        } else if (value.geocodedLocation) {
          try {
            const result = await searchLocation(
              value.label,
              value.geocodedLocation.displayPosition.Latitude,
              value.geocodedLocation.displayPosition.Longitude,
              {
                houseNumber: value.address.houseNumber || '',
                street: value.address.street,
                city: value.address.city,
                county: value.address.county,
                state: value.address.state,
                zipcode: value.address.postalCode,
              },
            );
            if (result) {
              setLocation(result);
            }
          } catch (error) {
            console.error('Error fetching enriched data:', error);
          }
        } else {
          // If enriched data is not available, set it as pending
          setPendingLocationUpdate(value);
        }
      } else {
        // This kills the pending search routine (if any are happening, e.g background tax search)
        setSelectedPrediction(null);
        setPendingLocationUpdate(null);
        setIsTaxSearchPending(false);

        // Here the agent clicks the (X) on the autocomplete
        // Lets reset the map to zoom out over the coverage area!
        setLatitude(tenantLocationCoverageCenter.x);
        setLongitude(tenantLocationCoverageCenter.y);
        setIsFirstMapInteraction(false);
        setMapZoom(10);
      }
    },
    [],
  );

  /**
   * Debounced function to search for addresses using the HERE API.
   * This reduces the number of API calls while typing.
   */
  const debouncedAddressSearch = useCallback(
    debounce(async (input: string) => {
      setIsLoading(true);
      setIsTaxSearchPending(false);
      try {
        const results = await searchAddressHere(
          HERE_API_KEY,
          input,
          buildBoundingBox(tenantLocationCoverageCenter),
        );

        // Set initial results immediately
        setAddressOptions(results);

        // Fetch geocoding and enriched data asynchronously
        const enrichedResults: EnrichedAutocompleteResult[] = await Promise.all(
          results.map(enrichHereResult),
        );

        // Only update if this is still the latest input
        if (input === latestInputRef.current) {
          if (input.length >= TAX_SEARCH_THRESHOLD && !isRealistPublicRecords) {
            setIsTaxSearchPending(true);
            searchLocationByTaxId(input).then(async (taxResults) => {
              if (input === latestInputRef.current) {
                const enrichedTaxResults = await Promise.all(
                  taxResults.map(enrichTaxResult),
                );

                // Combine HERE and tax results, remove duplicates
                const combinedResults = [
                  ...enrichedTaxResults, // Tax results are first
                  ...enrichedResults,
                ];
                const uniqueResults = Array.from(
                  new Set(combinedResults.map((a) => a.label)),
                ).map((label) =>
                  combinedResults.find((a) => a.label === label),
                );

                setAddressOptions(
                  uniqueResults as EnrichedAutocompleteResult[],
                );
                setIsTaxSearchPending(false);
              }
            });
          }

          setAddressOptions(enrichedResults);

          if (pendingLocationUpdate) {
            const updatedPrediction = enrichedResults.find((result) =>
              compareEnrichedResults(pendingLocationUpdate, result),
            );
            if (updatedPrediction && updatedPrediction.enrichedData) {
              if (updatedPrediction.geocodedLocation?.displayPosition) {
                const { Latitude, Longitude } =
                  updatedPrediction.geocodedLocation.displayPosition;
                setLatitude(Number(Latitude));
                setLongitude(Number(Longitude));
              }
              setLocation(updatedPrediction.enrichedData);
              updateAddressFields(updatedPrediction);
            }
            setPendingLocationUpdate(null);
          }
        }
      } catch (error) {
        setAddressOptions([]);
      } finally {
        setIsLoading(false);
      }
    }, 500), // Debounce time of 500ms
    [searchLocation, pendingLocationUpdate, setIsTaxSearchPending],
  );

  // Effect to trigger address search when input value changes (only for non-Realist or address mode)
  useEffect(() => {
    if (!isRealistPublicRecords || searchMode === 'address') {
      latestInputRef.current = inputValue;
      if (inputValue.trim().length >= 2) {
        debouncedAddressSearch(inputValue);
      } else {
        setAddressOptions([]);
      }
    }
  }, [inputValue, debouncedAddressSearch, isRealistPublicRecords, searchMode]);

  /**
   * Handles changes to the latitude or longitude input fields.
   * @param field The field being changed ('latitude' or 'longitude')
   * @param value The new value as a string
   */
  const handleCoordinateChange = (
    field: 'latitude' | 'longitude',
    value: string,
  ) => {
    const isValidInput = /^-?\d*\.?\d*$/.test(value);

    if (value === '') {
      // If the input is empty, clear the entire form
      if (field === 'latitude') {
        setLatitude(null);
      } else {
        setLongitude(null);
      }

      clear();
      return;
    }

    if (isValidInput) {
      let newLatitude = `${latitude}`;
      let newLongitude = `${longitude}`;

      if (field === 'latitude') {
        setLatitude(value);
        newLatitude = value;
      } else {
        setLongitude(value);
        newLongitude = value;
      }

      const latValue = Number.parseFloat(newLatitude);
      const lngValue = Number.parseFloat(newLongitude);

      if (isValidNumber(newLatitude) && isValidNumber(newLongitude)) {
        handleLocationChange('', latValue, lngValue);
      }
    }
  };

  const updateSystemProperties = useCallback(() => {
    const fields: Record<string, string> = {};
    if (tenantSystemLocale) {
      fields[PropertyField.SYSTEM_LOCALE] = tenantSystemLocale;
    }
    if (tenantSubSystemLocale) {
      fields[PropertyField.SUB_SYSTEM_LOCALE] = tenantSubSystemLocale;
    }
    if (tenantBusinessPartner) {
      fields[PropertyField.LISTING_SOURCE_BUSINESS_PARTNER] =
        tenantBusinessPartner;
    }
    updateFields(fields);
  }, [updateFields]);

  const handleNoTaxRecordLocationChange = useCallback(
    (lat: number | null, lng: number | null) => {
      const latStr = lat === null ? '' : lat.toString();
      const lngStr = lng === null ? '' : lng.toString();
      setLatitude(latStr);
      setLongitude(lngStr);

      const numericLat = Number.parseFloat(latStr.slice(0, MAX_LAT_LNG_LENGTH));
      const numericLng = Number.parseFloat(lngStr.slice(0, MAX_LAT_LNG_LENGTH));

      updateFields({
        LATITUDE: numericLat,
        LONGITUDE: numericLng,
      });
    },
    [setLatitude, setLongitude, updateFields],
  );

  const handleNoTaxRecordSave = useCallback(
    async (addressData: any) => {
      const latLngData = addressData['LATLONG_DATA'] || {};
      clear();
      setLocationType(latLngData.IS_ENABLED ? 'coordinates' : 'address');
      noTaxRecordModeRef.current = true;
      noTaxRecordMode.set();

      if (latLngData?.IS_ENABLED) {
        const latValue = Number.parseFloat(latLngData.LAT);
        const lngValue = Number.parseFloat(latLngData.LONG);
        handleCoordinateChange('latitude', latValue.toString());
        handleCoordinateChange('longitude', lngValue.toString());
        handleLocationChange('', latValue, lngValue);
        return;
      }

      let streetName = addressData[PropertyField.STREET_NAME];
      let streetSuffix = addressData[PropertyField.STREET_SUFFIX];
      let streetDirSuffix = addressData[PropertyField.STREET_DIR_SUFFIX];
      const streetNumber = addressData[PropertyField.STREET_NUMBER];
      const unitNumber = addressData[PropertyField.UNIT_NUMBER];
      const stateOrProvince = addressData[PropertyField.STATE_OR_PROVINCE];
      const county = addressData[PropertyField.COUNTY];
      const city = addressData[PropertyField.CITY];
      const postalCode = addressData[PropertyField.POSTAL_CODE];
      const formattedStreet = formatStreet(streetName, lookups);
      const streetNameIncludesSuffix = formattedStreet.suffix.length;
      const re = new RegExp(`(\\s+)(${formattedStreet.dir_suffix})(\\s+)?$`);
      const streetNameIncludesDirSuffix =
        formattedStreet.dir_suffix && streetName.match(re);

      if (streetNameIncludesSuffix || streetNameIncludesDirSuffix) {
        streetName = formattedStreet.name;
        // Only adds formatted suffix if is included in street name, is valid and
        // suffix input is empty
        if (streetNameIncludesDirSuffix && !streetDirSuffix) {
          streetDirSuffix = formattedStreet.dir_suffix;
        }

        if (!streetSuffix) {
          streetSuffix = formattedStreet.suffix;
        }
      }

      const streetAddress = formatAddressLineOne({
        streetNumber: streetNumber ?? '',
        streetName,
        streetDirPrefix: '',
        streetSuffix,
        streetDirSuffix,
        streetModifier: '',
        unitNumber,
      });

      const adddressToSearch = [
        streetNumber,
        streetName,
        city,
        stateOrProvince,
        postalCode,
      ]
        .filter(Boolean)
        .join(' ');

      const fullAdddress = [streetAddress, city, stateOrProvince, postalCode]
        .filter(Boolean)
        .join(' ');

      const locationData = {
        houseNumber: streetNumber || '',
        street: streetName || '',
        city: city || '',
        county: county || '',
        state: stateOrProvince || '',
        zipcode: postalCode || '',
      };

      try {
        const results = await searchAddressHere(
          HERE_API_KEY,
          adddressToSearch,
          buildBoundingBox(tenantLocationCoverageCenter),
        );

        // Fetch geocoding and enriched data asynchronously
        const enrichedResults: EnrichedAutocompleteResult[] = await Promise.all(
          results.map(enrichHereResult),
        );
        const [firstAddressOcurrence] = enrichedResults || [];

        if (firstAddressOcurrence) {
          setInputValue(fullAdddress);
          setSelectedPrediction(firstAddressOcurrence);

          const { geocodedLocation } = firstAddressOcurrence;
          if (geocodedLocation) {
            const lat = geocodedLocation?.displayPosition.Latitude;
            const lng = geocodedLocation?.displayPosition.Longitude;
            handleNoTaxRecordLocationChange(lat, lng);

            const locationResult = firstAddressOcurrence.enrichedData
              ? firstAddressOcurrence.enrichedData
              : await searchLocation(
                  fullAdddress || '',
                  lat,
                  lng,
                  locationData,
                );

            unparsedAddressField.updateValue(
              firstAddressOcurrence?.label || fullAdddress,
            );

            if (locationResult) {
              setLocation(locationResult);
            }
          }
        } else {
          const lat = tenantLocationOfficeCenter.x;
          const lng = tenantLocationOfficeCenter.y;
          const locationResult = await searchLocation(
            fullAdddress || '',
            lat,
            lng,
            locationData,
          );

          setInputValue(fullAdddress);
          showModal({
            title:
              "A map location could not be found automatically for the property's address.", // Optional title
            content: (
              <div>
                Please validate and update the address information or manually
                locate the property.
              </div>
            ),
            cancelText: 'Close',
            onCancel: () => {
              if (locationResult) {
                setLocation(locationResult);
              }
            },
          });

          handleNoTaxRecordLocationChange(lat, lng);
          setMapZoom(10);
          unparsedAddressField.updateValue(fullAdddress);
        }
      } catch (error) {
        console.error('Error fetching enriched data:', error);
      }

      updateFields({
        FULL_STREET_ADDRESS: fullAdddress,
        STREET_NUMBER: streetNumber,
        STREET_NAME: streetName,
        STREET_DIR_PREFIX: formattedStreet.dir_prefix || '',
        STREET_DIR_SUFFIX: streetDirSuffix || '',
        STREET_SUFFIX: streetSuffix || '',
        UNIT_NUMBER: unitNumber,
        STATE_OR_PROVINCE: stateOrProvince,
        COUNTY: county,
        CITY: city?.toUpperCase(),
        POSTAL_CODE: postalCode,
      });

      updateSystemProperties();
    },
    [
      clear,
      enrichHereResult,
      handleCoordinateChange,
      handleLocationChange,
      handleNoTaxRecordLocationChange,
      HERE_API_KEY,
      searchLocation,
      setLocation,
      showModal,
      updateFields,
      updateSystemProperties,
    ],
  );

  const formatAndUpdateStreet = useCallback(
    (unformattedStreet: string) => {
      const street = formatStreet(unformattedStreet, lookups);

      updateFields({
        STREET_NAME: street.name,
        STREET_DIR_PREFIX: street.dir_prefix,
        STREET_DIR_SUFFIX: street.dir_suffix,
        STREET_SUFFIX: street.suffix,
      });
    },
    [updateFields, lookups],
  );

  const updateAddressFields = useCallback(
    (address: EnrichedAutocompleteResult) => {
      const postal = formatZipCode(address.address.postalCode);
      const state = formatState(address.address.state);
      const county = formatCounty(
        address.address.county?.toUpperCase(),
        state,
        tenantCountyResolutionMap,
      );

      formatAndUpdateStreet(address.address.street);

      const updates: any = {
        STATE_OR_PROVINCE: state,
        CITY: address.address.city?.toUpperCase(),
        UNPARSED_ADDRESS: address.label,
        STREET_NUMBER: address.address.houseNumber,
        POSTAL_CODE: postal.base,
        FULL_STREET_ADDRESS: formatAddress(address),
      };

      if (county && state) {
        updates.COUNTY = `${county}-${state}`;
      }

      if (address.address.unitNumber) {
        updates.UNIT_NUMBER = address.address.unitNumber;
      }

      if (postal.plus4) {
        updates.POSTAL_CODE_PLUS4 = postal.plus4;
      }

      if (address.geocodedLocation?.displayPosition) {
        const { Latitude, Longitude } =
          address.geocodedLocation.displayPosition;
        updates.LATITUDE = Number(Latitude);
        updates.LONGITUDE = Number(Longitude);
      }

      updateFields(updates);
      updateSystemProperties();
    },
    [formatAndUpdateStreet, updateFields, updateSystemProperties],
  );

  /**
   * Specialized handler for map pin drag events.
   * In address mode, it only updates lat/long without affecting the address.
   * In coordinates mode or no-tax-record mode, it behaves like the regular location change.
   * @param address The address string (usually empty from drag events)
   * @param lat The new latitude
   * @param lng The new longitude
   */
  const handleMapPinDrag = useCallback(
    async (address: string, lat: number | null, lng: number | null) => {
      // Handle first interaction
      handleFirstMapInteraction();

      // If in no-tax-record mode, use the dedicated handler
      if (noTaxRecordModeRef.current) {
        return handleNoTaxRecordLocationChange(lat, lng);
      }

      // If in coordinates mode, use the regular handler
      if (locationType === 'coordinates') {
        return handleLocationChange(address, lat, lng);
      }

      // In address mode, only update lat/long without clearing or updating address
      handleNoTaxRecordLocationChange(lat, lng);
    },
    [
      locationType,
      handleFirstMapInteraction,
      handleNoTaxRecordLocationChange,
      handleLocationChange,
    ],
  );

  const renderTextFieldInput = useCallback(
    (params: any = {}, value: string = '') => {
      const textFieldParams = {
        ...params,
        ...(value.length ? { value } : {}),
        InputProps: {
          ...params.InputProps,
        },
      };

      // Determine placeholder based on Realist flow and search mode
      let placeholder = 'Enter Address or Tax ID';
      if (isRealistPublicRecords) {
        placeholder = searchMode === 'taxId' ? 'Enter Tax ID' : 'Enter Address';
      }

      return (
        <TextField
          {...textFieldParams}
          fullWidth
          placeholder={placeholder}
          variant="outlined"
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              '&.Mui-disabled': {
                backgroundColor: theme.palette.background.default, // Use default background
                '& input': {
                  color: theme.palette.text.disabled || theme.palette.grey[500],
                },
                '& fieldset': {
                  borderColor: theme.palette.grey[300],
                },
              },
            },
          }}
          InputProps={{
            ...textFieldParams.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {(isLoading || isTaxSearchPending) && (
                  <CircularProgress color="inherit" size={20} />
                )}
                {textFieldParams.InputProps.endAdornment}
              </>
            ),
          }}
        />
      );
    },
    [isLoading, isTaxSearchPending, isRealistPublicRecords, searchMode],
  );

  // Handle location type changes for mobile when in Realist mode
  const handleMobileLocationTypeChange = (value: string) => {
    if (isRealistPublicRecords && value === 'taxId') {
      setLocationType('taxId');
      setSearchMode('taxId');
    } else {
      setLocationType(value);
      if (value === 'address') {
        setSearchMode('address');
      }
    }
  };

  return (
    <FormAccordion
      title="Specify Your Listing Location"
      subtext={'Start Your Search'}
    >
      <Grid spacing={2} sx={{ p: 2 }}>
        {/* Conditionally render desktop or mobile layout based on actual screen size */}
        {!isMobile ? (
          // Desktop/Tablet Layout
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Address/Tax ID section */}
            <Grid size={{ md: 6, lg: 4 }}>
              {isRealistPublicRecords ? (
                <>
                  <AddressTaxIdSelector
                    searchMode={searchMode}
                    onSearchModeChange={setSearchMode}
                  />
                  {searchMode === 'address' ? (
                    <AddressSearchField
                      inputValue={inputValue}
                      addressOptions={addressOptions}
                      selectedPrediction={selectedPrediction}
                      isLoading={isLoading}
                      isTaxSearchPending={isTaxSearchPending}
                      locationType="address"
                      noTaxRecordModeRef={noTaxRecordModeRef}
                      handleAddressChange={handleAddressChange}
                      setInputValue={setInputValue}
                      renderTextFieldInput={renderTextFieldInput}
                      formatAddress={formatAddress}
                      compareEnrichedResults={compareEnrichedResults}
                      isRealistFlow={true}
                    />
                  ) : (
                    <TaxIdSearchField
                      inputValue={taxIdInputValue}
                      selectedCounty={selectedCounty}
                      isLoading={isTaxIdLoading}
                      locationType="taxId"
                      noTaxRecordModeRef={noTaxRecordModeRef}
                      taxOptions={taxOptions}
                      selectedPrediction={selectedPrediction}
                      onInputChange={setTaxIdInputValue}
                      onCountyChange={setSelectedCounty}
                      onSearch={handleTaxIdSearch}
                      handleAddressChange={handleAddressChange}
                      formatAddress={formatAddress}
                      compareEnrichedResults={compareEnrichedResults}
                    />
                  )}
                </>
              ) : (
                <>
                  <FormControlLabel
                    value="address"
                    control={
                      <Radio
                        data-testid={generateDataTestId(
                          PropertyField.LOCATION_TYPE,
                          undefined,
                          'radioField-address',
                        )}
                        checked={locationType === 'address'}
                        onChange={() => setLocationType('address')}
                      />
                    }
                    label="Address/Tax ID"
                  />
                  <AddressSearchField
                    inputValue={inputValue}
                    addressOptions={addressOptions}
                    selectedPrediction={selectedPrediction}
                    isLoading={isLoading}
                    isTaxSearchPending={isTaxSearchPending}
                    locationType={locationType}
                    noTaxRecordModeRef={noTaxRecordModeRef}
                    handleAddressChange={handleAddressChange}
                    setInputValue={setInputValue}
                    renderTextFieldInput={renderTextFieldInput}
                    formatAddress={formatAddress}
                    compareEnrichedResults={compareEnrichedResults}
                    isRealistFlow={false}
                  />
                </>
              )}

              {/* "Proceed without tax record" link */}
              {createWithoutTaxRecord && searchMode === 'address' && (
                <Box sx={{ mt: 1 }}>
                  <NoTaxRecordFormLink onSave={handleNoTaxRecordSave} />
                </Box>
              )}
            </Grid>
            {/* Latitude/Longitude section */}
            <Grid size={{ md: 6, lg: 4 }}>
              <FormControlLabel
                value="coordinates"
                control={
                  <Radio
                    data-testid={generateDataTestId(
                      PropertyField.LOCATION_TYPE,
                      undefined,
                      'radioField-coordinates',
                    )}
                    checked={locationType === 'coordinates'}
                    onChange={() => setLocationType('coordinates')}
                  />
                }
                label="Latitude/Longitude"
              />
              <CoordinatesField
                latitude={latitude}
                longitude={longitude}
                locationType={locationType}
                noTaxRecordModeRef={noTaxRecordModeRef}
                handleCoordinateChange={handleCoordinateChange}
              />
            </Grid>
          </Grid>
        ) : (
          // Mobile Layout
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12 }}>
              <RadioGroup
                row
                value={locationType}
                onChange={(e) => handleMobileLocationTypeChange(e.target.value)}
              >
                <FormControlLabel
                  value="address"
                  control={<Radio />}
                  label={isRealistPublicRecords ? 'Address' : 'Address/Tax ID'}
                />
                {isRealistPublicRecords && (
                  <FormControlLabel
                    value="taxId"
                    control={<Radio />}
                    label="Tax ID"
                  />
                )}
                <FormControlLabel
                  value="coordinates"
                  control={<Radio />}
                  label="Latitude/Longitude"
                />
              </RadioGroup>
            </Grid>
            {/* Only render one input section at a time based on selection */}
            {locationType === 'address' && (
              <Grid size={{ xs: 12 }}>
                <AddressSearchField
                  inputValue={inputValue}
                  addressOptions={addressOptions}
                  selectedPrediction={selectedPrediction}
                  isLoading={isLoading}
                  isTaxSearchPending={isTaxSearchPending}
                  locationType={locationType}
                  noTaxRecordModeRef={noTaxRecordModeRef}
                  handleAddressChange={handleAddressChange}
                  setInputValue={setInputValue}
                  renderTextFieldInput={renderTextFieldInput}
                  formatAddress={formatAddress}
                  compareEnrichedResults={compareEnrichedResults}
                  isRealistFlow={isRealistPublicRecords}
                />
              </Grid>
            )}
            {locationType === 'taxId' && isRealistPublicRecords && (
              <Grid size={{ xs: 12 }}>
                <TaxIdSearchField
                  inputValue={taxIdInputValue}
                  selectedCounty={selectedCounty}
                  isLoading={isTaxIdLoading}
                  locationType="taxId"
                  noTaxRecordModeRef={noTaxRecordModeRef}
                  taxOptions={taxOptions}
                  selectedPrediction={selectedPrediction}
                  onInputChange={setTaxIdInputValue}
                  onCountyChange={setSelectedCounty}
                  onSearch={handleTaxIdSearch}
                  handleAddressChange={handleAddressChange}
                  formatAddress={formatAddress}
                  compareEnrichedResults={compareEnrichedResults}
                />
              </Grid>
            )}
            {locationType === 'coordinates' && (
              <Grid size={{ xs: 12 }}>
                <CoordinatesField
                  latitude={latitude}
                  longitude={longitude}
                  locationType={locationType}
                  noTaxRecordModeRef={noTaxRecordModeRef}
                  handleCoordinateChange={handleCoordinateChange}
                />
              </Grid>
            )}
            {/* "Proceed without tax record" on mobile */}
            {createWithoutTaxRecord && locationType === 'address' && (
              <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                <NoTaxRecordFormLink onSave={handleNoTaxRecordSave} />
              </Grid>
            )}
          </Grid>
        )}

        {/* Map section - full width */}
        <Grid size={{ xs: 12 }} sx={{ mx: -1 }}>
          {isOptimizedEnabled ? (
            <HereMapsOptimized
              apiKey={HERE_API_KEY}
              latitude={
                isFirstMapInteraction && !isValidNumber(`${longitude}`)
                  ? tenantLocationCoverageCenter.x
                  : Number(latitude)
              }
              longitude={
                isFirstMapInteraction && !isValidNumber(`${latitude}`)
                  ? tenantLocationCoverageCenter.y
                  : Number(longitude)
              }
              zoom={mapZoom}
              onLocationChange={(address, lat, lng) => {
                return handleMapPinDrag(address, lat, lng);
              }}
              height={isMobile ? 400 : 800}
            />
          ) : (
            <HereMaps
              apiKey={HERE_API_KEY}
              latitude={
                isFirstMapInteraction && !isValidNumber(`${longitude}`)
                  ? tenantLocationCoverageCenter.x
                  : Number(latitude)
              }
              longitude={
                isFirstMapInteraction && !isValidNumber(`${latitude}`)
                  ? tenantLocationCoverageCenter.y
                  : Number(longitude)
              }
              zoom={mapZoom}
              onLocationChange={(address, lat, lng) => {
                return handleMapPinDrag(address, lat, lng);
              }}
              height={isMobile ? 400 : 800}
            />
          )}
        </Grid>
      </Grid>
    </FormAccordion>
  );
};

export default LocationSearch;
